import React from 'react';
import {ListChildComponentProps} from 'react-window';

import {InlineComboboxAddItemInput} from './InlineComboboxAddItemInput';
import {InlineComboboxItem} from './InlineComboboxItem';

export type ItemData<ItemType> = {
  disabled?: boolean;
  displayedItems: ItemType[];
  getItemProps: (arg: {item: ItemType; index: number}) => {};
  highlightedIndex: number;
  isAddingItem: boolean;
  itemToString: (item: ItemType) => string;
  newItemPlaceholder?: string;
  onAddItem: (itemName: string) => void;
  selectedItem: ItemType | null;
  setIsAddingItem: (adding: boolean) => void;
  isItemDisabled?: (item: ItemType) => boolean;
  disabledItemMessage?: (item: ItemType) => string | undefined;
};

export function ItemRenderer<ItemType>({
  index,
  style,
  data,
}: ListChildComponentProps<ItemData<ItemType>>) {
  const {
    disabled,
    getItemProps,
    highlightedIndex,
    isAddingItem,
    itemToString,
    newItemPlaceholder,
    onAddItem,
    selectedItem,
    setIsAddingItem,
    displayedItems,
    isItemDisabled,
    disabledItemMessage,
  } = data;

  // To get nice spacing around each item, we need to swizzle each item's
  // inline styling to account for the left- and top-padding. This is the
  // recommended way to do it from the react-window docs. Number casting here
  // is to make TypeScript happy.
  //
  // @see <https://codesandbox.io/p/sandbox/2w8wmlm89p>
  const styles = {
    ...style,
    top: Number(style.top) + 8,
    left: Number(style.left) + 8,
    height: Number(style.height) - 4,
    right: 8,
    width: 'auto',
  };

  // If we are adding an item, the first item in the scrolling container should
  // be the input field. Worth knowing that if the user clicks the "add item"
  // button, so is in the `isAddingItem` scope, and then scrolls down a long
  // list of items, it will unmount and remount this component, which could
  // lose a little bit of work-in-progress. Not an easy way to avoid this
  // behavior, though, without hoisting add-item intermediate state into the
  // InlineCombobox component.
  if (isAddingItem && index === 0) {
    return (
      <InlineComboboxAddItemInput
        key="$$ADDING_ITEM$$"
        disabled={disabled}
        style={styles}
        onAddItem={onAddItem}
        onCancelAddItem={() => setIsAddingItem(false)}
        placeholder={newItemPlaceholder}
      />
    );
  }

  // If we're adding an item, the react-window and downshift views of the
  // list of results are different, because if we're adding an item, an input
  // is rendered first, then the downshift list of results. So in that case,
  // the index of the react-window item and the downshift item will be offset
  // by -1.
  const itemIndex = isAddingItem ? index - 1 : index;
  const item = displayedItems[itemIndex];
  return (
    <div
      key={itemToString(item)}
      {...getItemProps({item, index: itemIndex})}
      style={styles}>
      <InlineComboboxItem
        disabled={isItemDisabled?.(item)}
        disabledItemMessage={disabledItemMessage?.(item)}
        item={item}
        itemToString={itemToString}
        selected={selectedItem === item}
        highlighted={highlightedIndex === itemIndex}
      />
    </div>
  );
}
